import * as React from 'react';
import * as ReactDom from 'react-dom';
import * as d3 from 'd3';
import './InclinationChart.css';

interface DichotomyChartChartProps {
    value: number;
    style?: string;
    small?: boolean;
}

interface DichotomyChartChartState {
    value: number;
    small?: boolean;
    previousSmall?: boolean; // we'll keep track of the previous isSmall state so that we can transition smoother.
}

export class DichotomyChart extends React.Component<DichotomyChartChartProps, DichotomyChartChartState> {
    private inclinationRect: d3.Selection<Element | d3.EnterElement | Document | Window | null, {}, null, undefined>;
    private inclinationText: d3.Selection<Element | d3.EnterElement | Document | Window | null, {}, null, undefined>;
    private barOffset = 0;

    // minimum values so the inital load doesn't look weird
    private minimumTextOffset = 12;
    private svgContainer: any = undefined;
    private element: any = undefined;
    private rects: any[] = [];
    // private rect1: any = undefined;

    constructor(props: DichotomyChartChartProps) {
        super(props);

        this.state = {
            value: this.props.value,
            small: this.props.small,
            previousSmall: undefined
        };
    }

    componentDidMount() {
        this.buildInclinationChart();
        this.updateInclinationChart(this.props.value, this.props.small);
        this.redraw.bind(this);
        window.addEventListener('resize', this.redraw.bind(this));
        window.addEventListener('side-menu-toggle', this.redraw.bind(this));
    }

    componentWillReceiveProps(nextProps: DichotomyChartChartProps) {
        this.setState({
            value: nextProps.value,
            small: nextProps.small,
            previousSmall: this.state.small
        });

        this.updateInclinationChart(nextProps.value, nextProps.small);
    }
    
    redraw() {
        setTimeout(
            () => {
                this.svgContainer
                    .attr('width', this.element.clientWidth)
                    .attr('height', this.element.clientHeight);
        
                this.updateInclinationChart(this.props.value);
                this.forceUpdate();
            }, 
            100);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.redraw.bind(this));
        window.removeEventListener('side-menu-toggle', this.redraw.bind(this));
    }

    buildInclinationChart() {
        this.element = ReactDom.findDOMNode(this);

        this.svgContainer = d3.select(this.element).append('svg')
            .attr('width', this.element.clientWidth)
            .attr('height', this.element.clientHeight);

        let adjustedClientWidth = this.element.clientWidth - this.barOffset;
        if (adjustedClientWidth < 0) { adjustedClientWidth = 0; }

        // more pronounced section background
        // this.rect1 = this.svgContainer.append('rect')
        //     .attr('x', 0)
        //     .attr('y', 7)
        //     .attr('width', adjustedClientWidth)
        //     .attr('height', this.element.clientHeight - 14)
        //     .attr('fill', 'rgba(0, 34, 62, 0.25)');
        
        var colors: string[] = ['#00223e', 'rgba(0, 34, 62, 0.75)', 'rgba(0, 34, 62, 0.5)', 'rgba(0, 34, 62, 0.25)', 'rgba(0, 34, 62, 0.15)', 'rgba(0, 34, 62, 0.25)', 'rgba(0, 34, 62, 0.5)', 'rgba(0, 34, 62, 0.75)', '#00223e'];
        
        var boxSize = adjustedClientWidth / 8.0;
        var padding = boxSize * 0.1;
        var boxSizePad = boxSize * 0.9;

        var i = 0;

        for (i = 0; i < 4; i++) {
            this.rects.push(this.svgContainer.append('rect')
                .attr('x', boxSize * i)
                .attr('y', 7)
                .attr('width', boxSizePad)
                .attr('height', this.element.clientHeight - 14)
                .attr('fill', colors[i]));
        }

        this.rects.push(this.svgContainer.append('rect')
            .attr('x', boxSize * 4 - 0.5)
            .attr('y', 7 - 4)
            .attr('width', 1)
            .attr('height', this.element.clientHeight - 14 + 8)
            .attr('fill', colors[0]));

        for (i = 0; i < 4; i++) {
            this.rects.push(this.svgContainer.append('rect')
                .attr('x', this.element.clientWidth - (boxSize * (4 - i)) + padding)
                .attr('y', 7)
                .attr('width', boxSizePad)
                .attr('height', this.element.clientHeight - 14)
                .attr('fill', colors[i + 1 + 4]));
        }        

        let circleY, circleWidth, circleHeight;
        if (this.props.small === true) {
            circleY = 5;
            circleWidth = this.element.clientHeight - 10;
            circleHeight = this.element.clientHeight - 10;
        } else {
            circleY = 0;
            circleWidth = this.element.clientHeight;
            circleHeight = this.element.clientHeight;
        }
        
        let newX = ((this.element.clientWidth) * 0.5) - 10;

        // inclination value rectangle
        this.inclinationRect = this.svgContainer.append('rect')
            .attr('x', newX)
            .attr('y', circleY)
            .attr('width', circleWidth )
            .attr('height', circleHeight )
            .attr('fill', '#b8271c')
            .attr('class', 'pva-style-circle ' + this.props.style);

        // inclination value text
        this.inclinationText = this.svgContainer.append('text')
            .attr('x', newX)
            .attr('y', this.element.clientHeight /  2.0 + 4)
            .attr('font-size', 12)
            .attr('font-weight', 'bold')
            .attr('fill', 'white')
            .text(0);
    }

    updateInclinationChart(inclinationValue: number, isSmall?: boolean) {
        // this.rect1.attr('width', this.element.clientWidth);

        let adjustedClientWidth = this.element.clientWidth - this.barOffset;
        if (adjustedClientWidth < 0) { adjustedClientWidth = 0; }

        var boxSize = adjustedClientWidth / 8.0;
        var padding = boxSize * 0.1;
        var boxSizePad = boxSize * 0.9;
        var i = 0;
        for (i = 0; i < 4; i++) {
            this.rects[i]
                .attr('x', boxSize * i)
                .attr('width', boxSizePad);
        }
        this.rects[4].attr('x', boxSize * 4 - 0.5);
        for (i = 0; i < 4; i++) {
            this.rects[i + 5]
                .attr('x', adjustedClientWidth - (boxSize * (4 - i)) + padding)
                .attr('width', boxSizePad);
        }

        var leftSide = false;
        if (inclinationValue < 0) {
            leftSide = true;
            inclinationValue = -inclinationValue;
        }

        // we're subtracting 1 from the inclination value because the bar to mark it is width 3
        // so we need to move it one back to the left so the center of it lines up with the inclination value
        let factor = inclinationValue - 1;

        if (factor < 0) { factor = 0; }

        // convert 0...100 into updated scale
        factor = this.convert(factor);

        factor /= 2.0;

        if (leftSide) {
            factor = 50.0 - factor;
        } else {
            factor = 50.0 + factor;
        }

        let newX = ((this.element.clientWidth) * (factor / 100)) - 14;
        if (newX > this.element.clientWidth - (this.element.clientHeight / 2.0) - 16) {
            newX = this.element.clientWidth - (this.element.clientHeight / 2.0) - 16;
        }

        if (newX < 0) {
            newX = 0;
        }

        let textOffset;
        if (inclinationValue < 9) {
            textOffset = isSmall !== true ? this.minimumTextOffset : this.minimumTextOffset - 5;
        } else {
            textOffset = isSmall !== true ? 8 : 4;
        }

        let circleY, circleWidth, circleHeight;
        if (isSmall === true) {
            circleY = 5;
            circleWidth = this.element.clientHeight - 10;
            circleHeight = this.element.clientHeight - 10;
        } else {
            circleY = 0;
            circleWidth = this.element.clientHeight;
            circleHeight = this.element.clientHeight;
        }

        this.inclinationRect.transition().duration(0)
            .attr('y', circleY)
            .attr('width', circleWidth)
            .attr('height', circleHeight);

        this.inclinationRect.transition().duration(2000)
            .attr('x', newX);

        this.inclinationText
            .transition().duration(2000)
            .tween('text', (d) => {
                const currentValue = Number(this.inclinationText.text());
                const interpolator = d3.interpolateNumber(currentValue, inclinationValue);

                return (t) => {
                    this.inclinationText.text(`${Math.round(interpolator(t))}`);
                };
            }).
            attr('x', newX + textOffset);
    }

    private convert(val: number) {
        var percent = 0.0;

        if (val === 0) {
            return 0;
        } else if (val > 0 && val <= 49) { // 0-25%
            percent = val / 49.0;
            return percent * 25.0;
        } else if (val >= 50 && val <= 69) { // 25-50%
            percent = (val - 50) / 20.0;
            return 25.0 + percent * 25.0;
        } else if (val >= 70 && val <= 89) { // 50-75%
            percent = (val - 70) / 20.0;
            return 50.0 + percent * 25.0;
        } else if (val >= 90 && val <= 100) { // 75-100%
            percent = (val - 90) / 10.0;
            return 75.0 + percent * 25.0;
        }

        return val;
    }

    render() {
        return (
            <div className="inclination-chart" />
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/charts/DichotomyChart.tsx